import React from 'react';
import { MapPin, ChevronDown, ShieldCheck, AlertTriangle, Users, ArrowDown } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from './ui/popover';
import { roleLabel, roleBadgeClass } from '../lib/roleBadges';

const RANK = { superadmin: 5, admin: 4, manager: 2, employee: 1 };

// Role badge: one colour set for the whole app (lib/roleBadges, C34).
const roleBadge = (role) => ({ cls: roleBadgeClass(role), label: roleLabel(role) });

const LOCATION_COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ec4899', '#0ea5e9', '#8b5cf6', '#ef4444', '#14b8a6'];

// Shared, so the `highlightIds` default doesn't hand every render a brand-new Set and
// invalidate everything memoised against it.
const NO_MATCHES = new Set();

export const LENS = { APPROVALS: 'approvals', REPORTING: 'reporting' };

// ── WHERE THE LINE LANGUAGE LIVES ───────────────────────────────────────────
// "Solid = you set this. Dashed = we worked it out."
//
// The lines are drawn as SVG paths by `measure()` below and styled by two CSS classes
// in index.css: `.orgtree-link` and `.orgtree-link-inferred`. To restyle every inferred
// line — colour, dash pattern, weight — change `.orgtree-link-inferred` and nothing
// else. The routing (which lane each kind takes) is in `measure()`.
//
// This replaced a constant that swapped between three CSS treatments. It went when the
// connectors moved from CSS borders to drawn paths: with real paths the interesting
// choice is the route, not the border-style.

// Distance the two trunks step apart when a parent has both kinds of edge leaving it.
const TRUNK_SPLIT = 8;

/**
 * Route one parent's edges to its children. Pure — box geometry in, SVG paths out —
 * so the rules can be tested without a browser.
 *
 * ROB'S ROUTING, agreed 30/07/2026. Solid edges share a rail; INFERRED edges take their
 * own lane above it AND their own trunk out of the parent. The guarantee that matters:
 * **a dashed path never shares a millimetre with a solid one**, so it stays dashed for
 * its whole length and you can follow it from a person to their approver.
 *
 * Two lanes rather than one per child, deliberately: a lane each would need vertical
 * room that grows with the size of the team, and nine reports under one manager is
 * already normal in Rob's data.
 */
export const linkPaths = (parentId, p, kids) => {
  if (!kids.length) return [];
  // ROUNDED, deliberately. Measured rects carry sub-pixel fractions that wobble as the
  // browser settles fonts and images. Unrounded, every wobble produces a different path
  // string, which is a new array, which is a re-render, which the ResizeObserver sees —
  // and the page never stops re-rendering. Whole pixels are also sharper on screen.
  const r = Math.round;
  const fromY = r(p.y + p.h);
  const cx = r(p.x + p.w / 2);
  const gap = Math.max(20, Math.min(...kids.map((k) => k.box.y)) - fromY);
  const lane = { inferred: r(fromY + gap * 0.34), set: r(fromY + gap * 0.68) };
  // Only fork the trunk when both kinds actually leave this parent — a chart with
  // nothing to distinguish shouldn't be given a fork to explain.
  const split = kids.some((k) => k.inferred) && kids.some((k) => !k.inferred);

  return kids.map((k) => {
    const trunkX = split ? cx + (k.inferred ? -TRUNK_SPLIT : TRUNK_SPLIT) : cx;
    const toX = r(k.box.x + k.box.w / 2);
    const toY = r(k.box.y);
    // Straight down when the child sits directly beneath: an elbow to nowhere looks
    // like a mistake.
    const d = Math.abs(toX - trunkX) < 1
      ? `M ${trunkX} ${fromY} V ${toY}`
      : `M ${trunkX} ${fromY} V ${k.inferred ? lane.inferred : lane.set} H ${toX} V ${toY}`;
    return { id: `${parentId}-${k.id}`, d, inferred: k.inferred };
  });
};

/**
 * "Their leave goes to, in order" — the chain as a numbered list.
 *
 * A chain is not a hierarchy, so it deliberately isn't drawn on the chart: a second
 * tier rendered as edges crosses half the diagram to say something a list says in one
 * line. Nobody reads a diagram to learn sequence.
 *
 * Opened by CLICK, not hover. The old version was a hover card, which doesn't exist on
 * a touch screen — and the same React app ships inside the Capacitor shell, so a
 * hover-only affordance is a feature that simply won't be there on the phone.
 */
const ApprovalDetail = ({ emp, chain, trigger }) => (
  // stopPropagation on the wrapper: the card behind this opens the employee dialog, and
  // clicking the badge means "tell me about the routing", not "let me edit them".
  <span onClick={(e) => e.stopPropagation()}>
    <Popover>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-3" onClick={(e) => e.stopPropagation()}>
        <p className="text-xs font-medium text-muted-foreground mb-2">
          {emp.first_name} {emp.last_name}&rsquo;s leave goes to, in order
        </p>
        <ol className="space-y-2">
          {chain.map((a, i) => (
            <li key={i} className="flex gap-2.5">
              <span className="w-5 h-5 mt-0.5 rounded-full bg-primary/15 text-primary text-[11px] font-bold flex items-center justify-center flex-shrink-0">
                {i + 1}
              </span>
              <span className="min-w-0">
                <span className="flex items-center gap-1.5 text-sm text-foreground">
                  {a.group && <Users className="w-3.5 h-3.5 flex-shrink-0 text-emerald-600" />}
                  <span className="truncate">{a.name}</span>
                </span>
                {/* The note carries the WHY — "their approver", "Any one of: …", or the
                    policy step's name. Without it a list of names says nothing about
                    where the routing came from, which is the question people arrive with. */}
                {a.note && (
                  <span className="block text-[11px] text-muted-foreground leading-snug">{a.note}</span>
                )}
              </span>
            </li>
          ))}
        </ol>
        <p className="text-[11px] text-muted-foreground mt-2.5 pt-2 border-t border-border flex items-start gap-1.5">
          <ArrowDown className="w-3 h-3 mt-0.5 flex-shrink-0" />
          {chain.length === 1 && chain[0] && chain[0].self
            ? 'Approved automatically when they book.'
            : 'If nobody acts, they’re chased and then it moves to an admin.'}
        </p>
      </PopoverContent>
    </Popover>
  </span>
);

const sortFn = (a, b) =>
  (RANK[b.role] || 0) - (RANK[a.role] || 0) ||
  (a.first_name || '').localeCompare(b.first_name || '');

// Break any parent cycle by rooting one of its members.
// The approvals lens can genuinely contain loops — two managers who approve each
// other is a perfectly ordinary setup — and without this both of them have a parent,
// neither is ever a root, and they silently vanish from the chart.
const cutCycles = (employees, parent) => {
  const state = new Map(employees.map((e) => [e.id, 0])); // 0 new, 1 on stack, 2 settled
  employees.forEach((e) => {
    const walked = [];
    let cur = e.id;
    while (cur && state.get(cur) === 0) {
      state.set(cur, 1);
      walked.push(cur);
      cur = parent.get(cur);
    }
    if (cur && state.get(cur) === 1) parent.set(cur, null); // re-entered: cut here
    walked.forEach((id) => state.set(id, 2));
  });
};

/**
 * Build the chart for ONE lens.
 *
 *   reporting  — an edge means "is their manager":  reports_to_id, then dept manager
 *   approvals  — an edge means "signs off their leave": whatever the backend resolved
 *
 * Same people, same boxes, different edges — that's the whole design. Callers get
 * `edgeMeta` alongside, saying for each person whether their edge was CHOSEN (solid)
 * or WORKED OUT (dashed), plus the chip text explaining which.
 *
 * Defaults to the reporting lens with no edge data, so the existing two-argument
 * callers (getManagerIds, the collapse-depth calculation) behave exactly as before.
 */
export const buildOrgTree = (employees = [], departments = [], opts = {}) => {
  const { lens = LENS.REPORTING, edges = {} } = opts;
  const byId = new Map(employees.map((e) => [e.id, e]));
  const deptById = new Map((departments || []).map((d) => [d.id, d]));
  const edgeMeta = new Map();
  const needsAttention = [];

  const reportingParent = (e) => {
    if (e.reports_to_id && e.reports_to_id !== e.id && byId.has(e.reports_to_id)) {
      edgeMeta.set(e.id, { inferred: false });
      return e.reports_to_id;
    }
    const dept = e.department_id ? deptById.get(e.department_id) : null;
    if (dept && dept.manager_id && dept.manager_id !== e.id && byId.has(dept.manager_id)) {
      edgeMeta.set(e.id, { inferred: true, chip: `via ${dept.name}` });
      return dept.manager_id;
    }
    return null;
  };

  const approvalsParent = (e) => {
    const edge = edges[e.id];
    if (!edge) return null;
    // No incoming edge, but emphatically not a problem: they approve their own leave.
    if (edge.self_approves) {
      edgeMeta.set(e.id, { inferred: false, self: true });
      return null;
    }
    // Flagged in the strip beneath the chart. NOT the same question as where they sit:
    // someone falling back to the admin backstop still has a real approver and belongs
    // under them, they just have nothing configured. Only a literal dead end has no
    // parent at all.
    if (edge.needs_attention) needsAttention.push({ emp: e, why: edge.why });

    // The spec said to lift dead ends out of the chart entirely, on the grounds that
    // giving them a position would mean inventing one. That reasoning assumed a
    // single-tree chart. This lens is a forest, so a parentless person is simply a root
    // and nothing is invented. Removing them would also be actively wrong: a dead end
    // can still APPROVE other people, and pulling them out orphans everyone beneath.
    if (edge.dead_end) return null;
    if (!edge.approver_id || !byId.has(edge.approver_id) || edge.approver_id === e.id) return null;
    const chip = edge.group_name
      || (edge.source === 'reports_to' ? 'same as manager'
        : edge.source === 'department' ? `via ${edge.via || 'their department'}`
          : edge.source === 'policy' ? `via ${edge.via || 'company policy'}`
            : edge.source === 'admin' ? 'admin backstop' : null);
    edgeMeta.set(e.id, { inferred: !!edge.inferred, chip, group: !!edge.group_name });
    return edge.approver_id;
  };

  const resolveParent = lens === LENS.APPROVALS ? approvalsParent : reportingParent;
  const parent = new Map();
  employees.forEach((e) => parent.set(e.id, resolveParent(e)));
  cutCycles(employees, parent);

  const inTree = employees;
  const rootish = inTree.filter((e) => !parent.get(e.id));
  const topLeader = rootish.slice().sort(sortFn)[0] || null;

  // REPORTING keeps the long-standing behaviour: anyone left without a parent hangs
  // under the top leader, so the chart is one tree.
  //
  // APPROVALS must NOT do that. Drawing a line from the top leader to someone who
  // approves their own leave would state something untrue — the spec's whole objection
  // to the old chart was that it invented relationships. So this lens is a forest:
  // several roots side by side, and top-level connectors are suppressed in CSS.
  if (lens !== LENS.APPROVALS && topLeader) {
    inTree.forEach((e) => {
      if (e.id === topLeader.id || parent.get(e.id)) return;
      parent.set(e.id, topLeader.id);
      // DASHED, and it must be. Nobody chose this — they simply have no manager, so the
      // chart parks them under whoever is top. Drawing it solid claimed a reporting line
      // that doesn't exist, which is the exact dishonesty the two-lens work set out to
      // remove. Caught on screen 30/07/2026, after the tests had gone green.
      edgeMeta.set(e.id, { inferred: true, chip: 'no manager set' });
    });
  }

  const children = new Map();
  inTree.forEach((e) => children.set(e.id, []));
  inTree.forEach((e) => {
    const p = parent.get(e.id);
    if (p && children.has(p) && p !== e.id) children.get(p).push(e);
  });
  children.forEach((list) => list.sort(sortFn));

  const roots = inTree.filter((e) => !parent.get(e.id)).sort(sortFn);

  return { topLeader, roots, children, edgeMeta, needsAttention };
};

export const getManagerIds = (employees, departments) => {
  const { children } = buildOrgTree(employees, departments);
  const ids = [];
  children.forEach((list, id) => { if (list.length) ids.push(id); });
  return ids;
};

// Defined at MODULE level, deliberately.
//
// This used to live inside OrgTree. A component declared inside another component is
// a brand-new function on every render, so React treats it as a different type and
// unmounts/remounts the entire tree each time — tearing down and re-creating every
// card ref in the process. The measuring code then reads a half-populated ref map,
// produces a different set of paths, sets state, and round it goes. It survived while
// the tree was simple; adding portalled popovers froze the browser outright when a
// location with eight reports was expanded (Rob, 31/07/2026).
//
// Everything it needs arrives in one memoised `ctx` bag, so the props identity is
// stable between renders too.
const Node = ({ emp, visited, ctx }) => {
  const {
    children, approverChains, edgeMeta, collapsedIds, locations, locationsEnabled,
    locationColor, highlightIds, lens, onOpen, onToggle, cardRefs, pendingCenterId,
  } = ctx;
    if (visited.has(emp.id)) return null; // cycle guard
    const kids = children.get(emp.id) || [];
    const chain = (approverChains[emp.id] && approverChains[emp.id].chain) || [];
    const meta = edgeMeta.get(emp.id) || {};
    const collapsed = collapsedIds.has(emp.id);
    const location = (locations || []).find((b) => b.id === emp.location_id);
    const rb = roleBadge(emp.role);
    const accent = (locationsEnabled && location) ? locationColor(emp.location_id) : '#94a3b8';
    const nextVisited = new Set(visited); nextVisited.add(emp.id);

    return (
      <li>
        <div
          ref={(el) => { if (el) cardRefs.current.set(emp.id, el); else cardRefs.current.delete(emp.id); }}
          className={`orgtree-card${highlightIds.has && highlightIds.has(emp.id) ? ' orgtree-card-match' : ''}`}
          style={{ borderTopColor: accent }}
          onClick={() => onOpen(emp.id)}
          data-testid={`orgtree-card-${emp.id}`}
        >
          <div className="flex items-center gap-2.5 text-left">
            {emp.profile_picture_url ? (
              <img src={emp.profile_picture_url} alt={`${emp.first_name} ${emp.last_name}`} className="w-9 h-9 rounded-full object-cover flex-shrink-0" />
            ) : (
              <div className="w-9 h-9 rounded-full bg-secondary flex items-center justify-center text-xs font-bold flex-shrink-0">
                {emp.first_name?.[0]}{emp.last_name?.[0]}
              </div>
            )}
            <div className="min-w-0">
              <p className="font-medium text-sm truncate">{emp.first_name} {emp.last_name}</p>
              <div className="flex items-center gap-1 mt-0.5">
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${rb.cls}`}>{rb.label}</span>
                {/* "2 steps", not a bare "2". The number alone was ambiguous — two
                    approvers? two days? — and the whole point of a multi-tier chain is
                    that it happens in sequence. */}
                {chain.length >= 2 && (
                  <ApprovalDetail
                    emp={emp}
                    chain={chain}
                    trigger={(
                      <button
                        type="button"
                        className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-600 text-[10px] font-semibold hover:bg-amber-500/25"
                        title="See the full approval chain"
                      >
                        <ShieldCheck className="w-3 h-3" /> {chain.length} steps
                      </button>
                    )}
                  />
                )}
                {chain.length === 1 && chain[0] && chain[0].self && (
                  <ApprovalDetail
                    emp={emp}
                    chain={chain}
                    trigger={(
                      <button
                        type="button"
                        className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-600 text-[10px] font-semibold hover:bg-emerald-500/25"
                        title="Approves their own leave"
                      >
                        <ShieldCheck className="w-3 h-3" /> Self
                      </button>
                    )}
                  />
                )}
              </div>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-1.5 truncate text-left">{emp.job_title || '—'}</p>
          {/* Who signs their leave off, in words. The line already shows it, but only if
              you can see both boxes at once — on a wide chart you usually can't. Reads
              from the display chain so a group shows as its name and a self-approver
              says so. Only in the approvals lens; it means nothing about reporting. */}
          {lens === LENS.APPROVALS && chain[0] && (
            <p className="text-[11px] mt-1 flex items-center gap-1 text-left text-muted-foreground truncate">
              <ShieldCheck className="w-3 h-3 flex-shrink-0 opacity-70" />
              <span className="truncate">
                {chain[0].self ? 'Approves own leave' : chain[0].name}
              </span>
            </p>
          )}
          {/* Says WHY this line is here when nobody chose it. Deliberately neutral, not
              amber: an inferred edge is the ordinary healthy case, and amber is reserved
              for the needs-attention strip. A group chip is solid-edged, since choosing
              a group IS a choice — it names the group rather than one member. */}
          {meta.chip && (
            <p className="text-left mt-1">
              {/* The chip is the natural thing to prod when you want to know who "A Team"
                  actually is (Rob's item 7). In the approvals lens it opens the same
                  panel as the steps badge; in the reporting lens it's only a label,
                  because a chain has nothing to do with reporting lines. */}
              {lens === LENS.APPROVALS && chain.length ? (
                <ApprovalDetail
                  emp={emp}
                  chain={chain}
                  trigger={(
                    <button
                      type="button"
                      className={`inline-block px-1.5 py-0.5 rounded-full text-[10px] border transition-colors ${
                        meta.group
                          ? 'bg-emerald-500/10 text-emerald-700 border-emerald-500/30 hover:bg-emerald-500/20'
                          : 'bg-secondary text-muted-foreground border-border hover:bg-secondary/70'}`}
                      title="See who approves this"
                    >
                      {meta.chip}
                    </button>
                  )}
                />
              ) : (
                <span className={`inline-block px-1.5 py-0.5 rounded-full text-[10px] border ${
                  meta.group
                    ? 'bg-emerald-500/10 text-emerald-700 border-emerald-500/30'
                    : 'bg-secondary text-muted-foreground border-border'}`}>
                  {meta.chip}
                </span>
              )}
            </p>
          )}
          {locationsEnabled && location && (
            <p className="text-[11px] mt-0.5 flex items-center gap-1 text-left" style={{ color: accent }}>
              <MapPin className="w-3 h-3 flex-shrink-0" /> {location.name}
            </p>
          )}
          {kids.length > 0 && (
            <button
              type="button"
              className="orgtree-toggle"
              onClick={(e) => { e.stopPropagation(); if (collapsed) pendingCenterId.current = emp.id; onToggle(emp.id); }}
              title={collapsed ? `Show ${kids.length} report${kids.length > 1 ? 's' : ''}` : 'Collapse'}
            >
              {collapsed ? `+${kids.length}` : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
          )}
        </div>
        {kids.length > 0 && !collapsed && (
          <ul>{kids.map((k) => <Node key={k.id} emp={k} visited={nextVisited} ctx={ctx} />)}</ul>
        )}
      </li>
    );
};

const OrgTree = ({ employees = [], departments = [], locations = [], locationsEnabled = false, collapsedIds, onToggle, onOpen, approverChains = {}, lens = LENS.APPROVALS, highlightIds = NO_MATCHES }) => {
  // approverChains is keyed by user id as { chain: [...], edge: {...} }. The `edge`
  // half is what positions and styles the approvals lens; `chain` is only for display.
  const edges = React.useMemo(() => {
    const out = {};
    Object.entries(approverChains || {}).forEach(([id, v]) => { if (v && v.edge) out[id] = v.edge; });
    return out;
  }, [approverChains]);

  // MEMOISED, and it must stay that way. buildOrgTree returns fresh Map instances every
  // call, so calling it straight from render gave `measure` a new identity on every
  // render, which re-ran the layout effect, which set state, which re-rendered...
  // "Maximum update depth exceeded" (Rob, 30/07/2026).
  const { roots, children, edgeMeta, needsAttention } = React.useMemo(
    () => buildOrgTree(employees, departments, { lens, edges }),
    [employees, departments, lens, edges],
  );

  // Memoised so `nodeCtx` below keeps its identity — an unstable function in there
  // would rebuild the context on every render and undo the whole point of it.
  const locationColor = React.useCallback((locationId) => {
    if (!locationId) return '#64748b';
    const idx = (locations || []).findIndex((b) => b.id === locationId);
    return idx >= 0 ? LOCATION_COLORS[idx % LOCATION_COLORS.length] : '#64748b';
  }, [locations]);

  const scrollRef = React.useRef(null);
  const canvasRef = React.useRef(null);
  const cardRefs = React.useRef(new Map());
  const pendingCenterId = React.useRef(null);
  const [links, setLinks] = React.useState([]);

  // ── ONE DRAWN PATH PER RELATIONSHIP ────────────────────────────────────────
  //
  // The old CSS connectors could only draw a single rail shared by all of a parent's
  // children, so a row came out half dashed whenever one sibling was inferred and
  // another wasn't, and no line could be followed from a person to their approver.
  //
  // Rob's routing (agreed 30/07/2026): solid edges share a rail; INFERRED edges get
  // their own lane above it, and their own trunk out of the parent. A dashed line is
  // therefore dashed for its whole length and never shares a millimetre with a solid
  // one. Capping it at two lanes matters — a lane per child would need vertical room
  // that grows with the team, and Bungle already has nine reports.
  const measure = React.useCallback(() => {
    const root = canvasRef.current;
    if (!root) return;
    const base = root.getBoundingClientRect();
    const box = (id) => {
      const el = cardRefs.current.get(id);
      if (!el) return null;
      const r = el.getBoundingClientRect();
      return { x: r.left - base.left, y: r.top - base.top, w: r.width, h: r.height };
    };

    const out = [];
    children.forEach((kids, parentId) => {
      if (!kids.length || collapsedIds.has(parentId)) return;
      const p = box(parentId);
      if (!p) return;
      const visible = kids.map((k) => ({ k, b: box(k.id) })).filter((x) => x.b);
      if (!visible.length) return;

      out.push(...linkPaths(parentId, p, visible.map(({ k, b }) => ({
        id: k.id, box: b, inferred: !!(edgeMeta.get(k.id) || {}).inferred,
      }))));
    });

    // Bail out when nothing actually moved, or React gets a brand-new array every time
    // and renders again for nothing — which, with a ResizeObserver watching, never
    // settles.
    setLinks((prev) => (prev.length === out.length
      && prev.every((l, i) => l.id === out[i].id && l.d === out[i].d
        && l.inferred === out[i].inferred)
      ? prev : out));
  }, [children, edgeMeta, collapsedIds]);

  React.useLayoutEffect(() => {
    // Two passes: once immediately, once after the browser has settled fonts and
    // avatars, which change card heights and would otherwise leave lines short.
    measure();
    const t = setTimeout(measure, 120);
    return () => clearTimeout(t);
  }, [measure, employees, lens]);

  React.useEffect(() => {
    const root = canvasRef.current;
    if (!root || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => measure());
    ro.observe(root);
    window.addEventListener('resize', measure);
    return () => { ro.disconnect(); window.removeEventListener('resize', measure); };
  }, [measure]);
  React.useEffect(() => {
    // Centre the viewport on the root once the tree has laid out.
    const t = setTimeout(() => {
      const el = scrollRef.current;
      if (el) el.scrollLeft = Math.max(0, (el.scrollWidth - el.clientWidth) / 2);
    }, 60);
    return () => clearTimeout(t);
  }, []);

  // Scroll the first search match into view. Highlighting someone off-screen is the
  // same as not finding them at all.
  const firstMatch = React.useMemo(
    () => employees.find((e) => highlightIds.has && highlightIds.has(e.id))?.id || null,
    [employees, highlightIds],
  );
  React.useEffect(() => {
    if (!firstMatch) return;
    const t = setTimeout(() => {
      const card = cardRefs.current.get(firstMatch);
      if (card) card.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }, 120);
    return () => clearTimeout(t);
  }, [firstMatch]);

  React.useEffect(() => {
    // When a node is expanded, centre it (and its new subtree) in the viewport.
    const id = pendingCenterId.current;
    if (!id) return;
    pendingCenterId.current = null;
    const t = setTimeout(() => {
      const container = scrollRef.current;
      const card = cardRefs.current.get(id);
      if (container && card) {
        const cRect = container.getBoundingClientRect();
        const kRect = card.getBoundingClientRect();
        const delta = (kRect.left + kRect.width / 2) - (cRect.left + cRect.width / 2);
        container.scrollTo({ left: container.scrollLeft + delta, behavior: 'smooth' });
      }
    }, 80);
    return () => clearTimeout(t);
  }, [collapsedIds]);

  // One stable object for the whole tree. Passing thirteen separate props would work
  // just as well, but every one of them would need memoising individually to keep Node
  // from re-rendering the world on each keystroke in the search box.
  const nodeCtx = React.useMemo(() => ({
    children, approverChains, edgeMeta, collapsedIds, locations, locationsEnabled,
    locationColor, highlightIds, lens, onOpen, onToggle, cardRefs, pendingCenterId,
  }), [children, approverChains, edgeMeta, collapsedIds, locations, locationsEnabled,
       locationColor, highlightIds, lens, onOpen, onToggle]);

  if (!roots.length) {
    return <div className="text-sm text-muted-foreground p-6">No employees to display yet.</div>;
  }

  return (
    <>
    <div ref={scrollRef} className="overflow-x-auto pb-6" data-testid="org-tree-view">
      {/* `orgtree-searching` dims everything that isn't a match — the single biggest
          reason a found card is findable. Only applied when there's actually something
          to find, so the chart never sits there greyed out for no reason. */}
      <div
        ref={canvasRef}
        className={`orgtree inline-block min-w-full px-4${
          highlightIds.size ? ' orgtree-searching' : ''}`}
        style={{ position: 'relative' }}
      >
        {/* The line layer, behind the cards. Not focusable and not hit-testable — it is
            a picture of the relationships, the cards are the controls. */}
        {/* Sized in PERCENTAGES, never in pixels measured from its own parent.
            An absolutely-positioned child counts towards its container's scroll size,
            so setting width={container.scrollWidth} made the container wider, which
            made the SVG wider, and so on — a layout feedback loop that produced a
            mile-wide empty chart (Rob, 30/07/2026). At 100% it can't outgrow its
            container, and overflow:visible lets paths paint past the edge anyway. */}
        <svg
          width="100%"
          height="100%"
          style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none', overflow: 'visible' }}
          aria-hidden="true"
          data-testid="orgtree-links"
        >
          {links.map((l) => (
            <path key={l.id} d={l.d}
              className={`orgtree-link${l.inferred ? ' orgtree-link-inferred' : ''}`} />
          ))}
        </svg>
        {/* Several roots are normal in the approvals lens — anyone who approves their
            own leave has no incoming edge, so nothing joins them. */}
        <ul className="orgtree-roots">
          {roots.map((r) => <Node key={r.id} emp={r} visited={new Set()} ctx={nodeCtx} />)}
        </ul>
      </div>
      </div>
      {/* OUTSIDE the horizontally-scrolling area — inside it, the strip stretched to
          the full width of the chart content and scrolled away sideways with it.
          Grouped by reason, because they call for different fixes: "no approver set"
          means go and set one, a broken group means go and add someone to it. Only
          shown in the approvals lens — none of it is meaningful about reporting. */}
      {/* RED, not amber (Rob's call, 30/07/2026). This isn't a gentle note — someone's
          leave is going to land on whoever the database returns first, and that's a
          "sort this out" message. Amber was reading as decoration next to everything
          else on the page. Kept as a left bar plus a solid border so it carries weight
          without becoming a full red panel that makes a healthy chart look on fire. */}
      {lens === LENS.APPROVALS && needsAttention.length > 0 && (
        <div
          className="mx-4 mt-3 rounded-lg border-2 border-rose-500/70 bg-rose-500/10 border-l-8 border-l-rose-600 px-4 py-3.5"
          data-testid="orgtree-needs-attention"
          role="alert"
        >
          <p className="text-base font-semibold text-rose-700 dark:text-rose-400 flex items-center gap-2 mb-2">
            <AlertTriangle className="w-5 h-5 flex-shrink-0" />
            {needsAttention.length} {needsAttention.length === 1 ? 'person needs' : 'people need'} attention
          </p>
          {Object.entries(
            needsAttention.reduce((acc, n) => {
              (acc[n.why] = acc[n.why] || []).push(n.emp);
              return acc;
            }, {}),
          ).map(([why, people]) => (
            <p key={why} className="text-sm text-rose-700/90 dark:text-rose-400/90 ml-7 leading-relaxed">
              {why} —{' '}
              {people.map((e, i) => (
                <React.Fragment key={e.id}>
                  {i > 0 && ', '}
                  <button
                    type="button"
                    className="font-medium underline underline-offset-2 decoration-2 hover:no-underline"
                    onClick={() => onOpen(e.id, { needsApproval: true })}
                  >
                    {e.first_name} {e.last_name}
                  </button>
                </React.Fragment>
              ))}
            </p>
          ))}
        </div>
      )}
    </>
  );
};

export default OrgTree;
